<template>
  <div class="product-carousel" :class="[background]">
    <div v-if="loading" class="product-carousel-skeletons">
      <SfSkeleton
        v-for="index in carouselSettings.perView"
        :key="index"
        type="image"
        class="product-carousel-skeleton"
      />
    </div>
    <div v-else>
      <div v-if="!isDesktop">
        <HeroSliderBannerSplitColorsVue
          class="carousel-title"
          :black-part="title.black"
          :red-part="title.red"
        />
      </div>
      <SfCarousel :settings="carouselSettings">
        <slot>
          <SfCarouselItem v-for="(product, key) in mappedProducts" :key="key">
            <SfProductCardCustom
              v-if="itemsInWishlist"
              :product-entity="product"
              :is-in-wishlist="!!itemsInWishlist[product.sku]"
              @clickWishlist="toggleWishlist(product, mappedProducts)"
            />
          </SfCarouselItem>
        </slot>
        <template #next="{ go }" v-if="isDesktop">
          <ArrowRightDark
            class="product-carousel-arrow product-carousel-arrow-right"
            alt="ArrowRightDark"
            @click="go('next')"
          />
        </template>
        <template #prev="{ go }" v-if="isDesktop">
          <HeroSliderBannerSplitColorsVue
            class="carousel-title"
            :black-part="title.black"
            :red-part="title.red"
          />
          <ArrowLeftDark
            class="product-carousel-arrow product-carousel-arrow-left"
            alt="ArrowLeftDark"
            @click="go('prev')"
          />
        </template>
      </SfCarousel>
    </div>
  </div>
</template>

<script>
import SfProductCardCustom from '~/components/SfProductCardCustom.vue';
import { SfCarousel } from '@storefront-ui/vue';

import { productGetters, useUser } from '@gemini-vsf/composables';
import {
  computed,
  defineComponent,
  onMounted,
  useContext,
} from '@nuxtjs/composition-api';
import { useAddToCart } from '~/helpers/cart/addToCart';
import { useImage, useUrlHelpers, useWishlist } from '~/composables';
import HeroSliderBannerSplitColorsVue from '~/components/HeroSlider/HeroSliderBannerSplitColors.vue';
import { ArrowLeftDark, ArrowRightDark } from '~/static/icons';

export default defineComponent({
  name: 'ProductsCarousel',
  components: {
    SfCarousel,
    SfProductCardCustom,
    HeroSliderBannerSplitColorsVue,
    ArrowLeftDark,
    ArrowRightDark,
  },
  props: {
    title: {
      type: Object,
      required: false,
      default: () => {},
    },
    products: {
      type: Array,
      required: false,
      default: () => [],
    },
    background: {
      type: String,
      required: false,
      default: 'white',
    },
    loading: Boolean,
    carouselSettings: {
      type: Object,
      required: false,
      default: () => {
        return { peek: 16, breakpoints: { 1023: { peek: 0, perView: 2 } } };
      },
    },
    isDesktop: Boolean,
  },
  setup(props) {
    const { app } = useContext();
    const { buildProductUrl } = useUrlHelpers();
    const { isAuthenticated } = useUser();
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const { loadItemsFromList, itemsInWishlist, toggleWishlist } =
      useWishlist();

    const mappedProducts = computed(() =>
      props.products.map((product) => product)
    );

    onMounted(async () => {
      await loadItemsFromList(mappedProducts.value);
    });

    const { getGeminiImage, imageSizes } = useImage();

    return {
      isAuthenticated,
      itemsInWishlist,
      mappedProducts,
      productGetters,
      getGeminiImage,
      imageSizes,
      buildProductUrl,
      toggleWishlist,
    };
  },
});
</script>

<style lang="scss">
.product-carousel {
  padding-top: 170px;
  padding-bottom: 180px;

  &.grey {
    background-color: var(--c-grey-1);
  }

  .product-carousel-skeletons {
    display: flex;
    gap: 10px;
    justify-content: space-around;
    .product-carousel-skeleton {
      height: 630px;
      width: 306px;
    }
  }

  .carousel-title.hero-slider-banner-split-colors {
    padding: 50px 0 0 0;
    font-family: var(--font-family--primary);
    font-size: var(--font-size--sm);
    letter-spacing: -1.2px;
    line-height: var(--font-size--2xl);
    margin-right: auto;
  }

  .sf-carousel {
    .sf-carousel__wrapper {
      margin: 0;
      padding: 0 15px;
      max-width: var(--width-boxed);
    }

    .sf-carousel__controls {
      top: -85px;
      justify-content: flex-end;
      align-items: center;

      .product-carousel-arrow-left {
        margin-right: 15px;
      }
    }
  }

  .product-carousel-arrow {
    box-shadow: none;
    padding: 0;
    width: 44px;
    height: 44px;
    border: 1px solid var(--c-grey-2);
    background-color: var(--c-white);
    border-radius: 3px;
    &:hover {
      background-color: var(--c-grey-9);
      cursor: pointer;
    }
  }
}

//big desktop
@media (min-width: $desktop-xxl-min) {
  .product-carousel {
    margin: 0 auto;
    padding-left: var(--spacer-base);
    padding-right: var(--spacer-base);
  }
  .sf-carousel {
    max-width: var(--width-boxed);
    margin: 0 auto;
    .sf-carousel__controls {
      display: flex !important;
    }
  }
}

//small desktop
@media (min-width: $desktop-min) and (max-width: $desktop-xxl-min - 1) {
  .product-carousel {
    margin: 0 auto;
  }
  .sf-carousel {
    width: 80%;
    margin: 0 auto;
    .sf-carousel__controls {
      display: flex !important;
    }
  }
}

//tablet
@media (min-width: $tablet-min) and (max-width: $tablet-max) {
  .product-carousel {
    padding: 50px var(--tablet-margin) 105px;
    .carousel-title.hero-slider-banner-split-colors {
      font-size: var(--font-size--sm);
      width: fit-content;
      margin: 0 auto;
      padding: 50px 0;
    }
  }
}

//mobile
@media (max-width: $mobile-max) {
  .product-carousel {
    padding: 50px var(--mobile-margin) 105px;
    .carousel-title.hero-slider-banner-split-colors {
      font-size: var(--font-size--sm);
      width: fit-content;
      margin: 0 auto;
      padding: 50px 0;
    }
  }
}
</style>
